import { BRAND } from '@/config/brand';
import { StorageError, type StorageAdapter } from './StorageAdapter';

/** Default v1 adapter. Writes are debounced by the browser, reads are cached by the OS. */
export class LocalStorageAdapter implements StorageAdapter {
  constructor(private readonly storage: Storage = window.localStorage) {}

  static isAvailable(): boolean {
    try {
      const probe = BRAND.storageProbeKey;
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      return true;
    } catch {
      return false;
    }
  }

  async read<T>(key: string): Promise<T | null> {
    try {
      const raw = this.storage.getItem(key);
      return raw === null ? null : (JSON.parse(raw) as T);
    } catch (cause) {
      throw new StorageError(`Unable to read "${key}" from local storage.`, cause);
    }
  }

  async write<T>(key: string, value: T): Promise<void> {
    try {
      this.storage.setItem(key, JSON.stringify(value));
    } catch (cause) {
      throw new StorageError(
        'Local storage is full. Remove a few old quotes or archive unused services.',
        cause,
      );
    }
  }

  async remove(key: string): Promise<void> {
    this.storage.removeItem(key);
  }

  async keys(): Promise<string[]> {
    return Array.from({ length: this.storage.length }, (_, index) => this.storage.key(index)).filter(
      (key): key is string => key !== null,
    );
  }
}
